import React, { useState } from 'react';
import moment from 'moment';
import ButtonMedium from 'components/common/Buttons/ButtonMedium';
import CloseButton from 'components/common/CloseButton';
import {
  CleanScreenResultType,
  UrineDrugScreenDto,
} from '@peartherapeutics/hylia-api-client';
import { shiftDate } from 'utils/dateTime';
import Calendar from '../Calendar';
import styles from './styles.module.scss';

export interface IModalProps {
  urineDrugScreens: UrineDrugScreenDto[];
  modalIsOpen: boolean;
  arStartDate: string;
  arTimezone: string;
  addCleanScreenStatus: (param: {
    date: string;
    type: CleanScreenResultType;
  }) => void;
  hideModal: () => void;
}

const ModalUrineDrugScreen = (props: IModalProps) => {
  const excludeDates = props.urineDrugScreens.map((uds) =>
    shiftDate(new Date(uds.date), props.arTimezone),
  );
  const udsExistsForToday = props.urineDrugScreens.some((uds) =>
    moment(uds.date)
      .tz(props.arTimezone)
      .isSame(moment().tz(props.arTimezone), 'date'),
  );
  const initialSelectedDate = udsExistsForToday
    ? null
    : shiftDate(new Date(), props.arTimezone);
  const [step, setStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    initialSelectedDate,
  );

  const goToNextStep = () => setStep(1);

  const handleUpdateCleanScreenStatus = (type: CleanScreenResultType) => () => {
    if (props.modalIsOpen && selectedDate) {
      props.addCleanScreenStatus({ date: selectedDate.toISOString(), type });
      props.hideModal();
    }
  };

  return step ? (
    <div data-testid="select-status-modal">
      <div className={styles['modal-title']}>Enter Patient Result</div>
      <CloseButton onClick={props.hideModal} />
      <div className={styles['modal-subtitle']}>
        Did the patient complete a screen?
      </div>
      <div className={styles['action-container']}>
        <div className={styles['action-item']}>
          <div
            className={styles['negative-button']}
            onClick={handleUpdateCleanScreenStatus(
              CleanScreenResultType.Negative,
            )}
            data-testid="uds-modal-negative-button"
          >
            <div className={styles['negative-icon']} />
            <div>Completed</div>
          </div>
          <div className={styles.description}>
            The patient will receive an extra reward in the app
          </div>
        </div>
        <div className={styles['action-item']}>
          <div
            className={styles['positive-button']}
            onClick={handleUpdateCleanScreenStatus(
              CleanScreenResultType.Positive,
            )}
            data-testid="uds-modal-positive-button"
          >
            <div className={styles['positive-icon']} />
            <div>Not Completed</div>
          </div>
          <div className={styles.description}>
            The patient will not receive any reward
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div
      className={styles['modal-select-date']}
      data-testid="select-date-modal"
    >
      <div>
        <div className={styles['modal-title']}>Select Date of Screen</div>
        <CloseButton
          onClick={props.hideModal}
          data-testid="close-modal-button"
        />
        <Calendar
          selectedDate={selectedDate}
          excludeDates={excludeDates}
          onSelectDate={setSelectedDate}
          minDate={shiftDate(new Date(props.arStartDate), props.arTimezone)}
          maxDate={shiftDate(new Date(), props.arTimezone)}
        />
      </div>
      <div>
        <ButtonMedium
          buttonRole="primary"
          width="100%"
          handleClick={goToNextStep}
          disabled={!selectedDate}
          data-testid="uds-modal-next-button"
        >
          NEXT
        </ButtonMedium>
      </div>
    </div>
  );
};

export default ModalUrineDrugScreen;
